import { RedRobinApiService } from '@api_patient/red_robin_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { useAnalyticContext } from '@application/analytic/analytic_context.provider';
import { AuthService } from '@application/authentication/auth.service';
import { DateService } from '@application/time/date.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { RouterService } from '@cortex-shim/router.service';
import { getAbsoluteRoute } from '@module/get_absolute_route';
import { useQueryClient } from '@tanstack/react-query';
import {
	type ClientConditionApiModel,
	clientConditionsListOptions,
	clientUnitsGetOptions,
	NavigationBookingSubjectRelation,
	type PhysicalLocationApiModel,
	type SearchLimitationResult,
	searchLimitationGetSearchLimitationOptions,
	unitsPhysicalLocationsGetPhysicalLocationsOptions,
} from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { NiN } from '@visiba/national-identity-number';
import { safeNumberParse } from '@visiba/std';
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBookingBasket } from './use_booking_basket';
import { type BookingSettings, useBookingSettings } from './use_booking_settings';

export enum BookingScreen {
	WhoIsRespondant = 'who-is-respondant',
	ReasonForVisit = 'reason-for-visit',
	Location = 'location',
	TimeSlot = 'time-slot',
	Forms = 'forms',
	Access = 'access',
	Conclusion = 'conclusion',
	Done = 'done',
	Error = 'error',
}

interface FlowData {
	currentCountry: keyof NiN.Implementations;
	allowRepresentative: boolean;
	useReasonForVisits: boolean;
	reasonForVisits: ClientConditionApiModel[];
	physicalLocations: PhysicalLocationApiModel[];
}

interface FlowSettings extends BookingSettings {
	searchContext: Nullable<SearchLimitationResult>;
}

export type FlowSettingsError = 'NotFound' | 'Unauthorized' | 'Unknown';

type AsyncState<T, E = null> = { value: null; status: 'pending' } | { value: E; status: 'error' } | { value: T; status: 'accessible' };

export interface ViewBookingContextValue {
	flowData: AsyncState<FlowData>;
	flowSettings: AsyncState<FlowSettings, FlowSettingsError>;
	goToScreen(screen: BookingScreen, options?: GoToScreenOptions): void;
	goToReceptionHome(): void;
	setupNewBasket(): Promise<void>;
	abortBooking(): Promise<void>;
	loadSettings(): Promise<AsyncState<FlowSettings, FlowSettingsError>>;
	getFlowSettings(): FlowSettings;
	getFlowData(): FlowData;
	getFilteredReasonForVisits(): ClientConditionApiModel[];
	getSelectedReasonForVisit(): Nullable<ClientConditionApiModel>;
}

interface GoToScreenOptions {
	replace?: boolean;
	locationType?: 'physical' | 'digital';
	locationId?: number;
	formId?: number;
	formPage?: number;
	ticket?: string;
	reschedule?: boolean;
	autoForwardedFromLocation?: boolean;
}

const ViewBookingContext = createContext<ViewBookingContextValue | null>(null);

export function useBooking(): ViewBookingContextValue {
	const context = useContext(ViewBookingContext);
	if (context == null) {
		throw new Error('useViewBooking must be used within a <ViewBookingProvider>');
	}
	return context;
}

export function BookingProvider({ children }: PropsWithChildren) {
	const [searchParams] = useSearchParams();
	const analytic = useAnalyticContext();
	const routerService = useResolve(RouterService);
	const queryClient = useQueryClient();
	const universalConfig = useResolve(UniversalConfig);
	const unitService = useResolve(UnitService);
	const analyticService = useResolve(AnalyticService);
	const bookingBasket = useBookingBasket();
	const authService = useResolve(AuthService);
	const bookingSettings = useBookingSettings();
	const redRobinApiService = useResolve(RedRobinApiService);
	const dateService = useResolve(DateService);
	const loggerRef = useRef(new Logger('useViewBooking'));
	const [flowData, setFlowData] = useState<AsyncState<FlowData>>({ value: null, status: 'pending' });
	const [flowSettings, setFlowSettings] = useState<AsyncState<FlowSettings, FlowSettingsError>>({
		value: null,
		status: 'pending',
	});
	const unit = unitService.getUnitNodeInTree(universalConfig.selectedUnitId);
	const routeBase = unit.routeWithoutLicenceholder;

	const fetchReasonForVisits = useCallback(async (): Promise<ClientConditionApiModel[]> => {
		loggerRef.current.logInformation('Get reason for visits');
		try {
			const unitConfigResult = await queryClient.fetchQuery(
				clientUnitsGetOptions({ path: { unitId: universalConfig.selectedUnitId, platform: 'web' } }),
			);
			if (unitConfigResult.Data?.Offer?.HasConditionWorkflow === false) return [];
			const reasonForVisitsResult = await queryClient.fetchQuery(
				clientConditionsListOptions({ path: { unitId: universalConfig.selectedUnitId } }),
			);
			return reasonForVisitsResult.Data ?? [];
		} catch (error) {
			loggerRef.current.logError(
				'User tried to get reason for visits: {message} {statusCode}',
				error instanceof Error ? error.message : 'Unknown error',
				undefined,
			);
			throw error;
		}
	}, [queryClient, universalConfig.selectedUnitId]);

	const fetchPhysicalLocations = useCallback(async (): Promise<PhysicalLocationApiModel[]> => {
		loggerRef.current.logInformation('Get physical locations');
		try {
			const unitConfigResult = await queryClient.fetchQuery(
				clientUnitsGetOptions({ path: { unitId: universalConfig.selectedUnitId, platform: 'web' } }),
			);
			if (unitConfigResult.Data?.Offer?.HasPhysicalLocation === false) return [];
			const physicalLocationResult = await queryClient.fetchQuery(
				unitsPhysicalLocationsGetPhysicalLocationsOptions({
					path: { unitId: universalConfig.selectedUnitId },
					query: { languageCode: universalConfig.selectedLanguageCode.peek() },
				}),
			);
			return physicalLocationResult.Data ?? [];
		} catch (error) {
			loggerRef.current.logError(
				'User tried to get physical locations: {message} {statusCode}',
				error instanceof Error ? error.message : 'Unknown error',
				undefined,
			);
			throw error;
		}
	}, [queryClient, universalConfig]);

	const goToScreen = useCallback(
		(screen: BookingScreen, options: GoToScreenOptions = {}): void => {
			let path = '';
			const analyticActionName = screen;
			switch (screen) {
				case BookingScreen.WhoIsRespondant:
					path = 'who-is-respondant';
					break;
				case BookingScreen.ReasonForVisit:
					path = 'reason-for-visit';
					break;
				case BookingScreen.Location:
					path = 'location';
					break;
				case BookingScreen.TimeSlot:
					path = 'time-slot';
					if (options.locationType === 'digital') path += '/digital';
					else if (options.locationType === 'physical' && options.locationId) path += `/${options.locationId}`;
					break;
				case BookingScreen.Forms:
					path = 'forms';
					if (options.formId) {
						path += `/${options.formId}`;
						if (options.formPage) path += `/${options.formPage}`;
					}
					break;
				case BookingScreen.Access:
					path = 'access';
					break;
				case BookingScreen.Conclusion:
					path = 'conclusion';
					break;
				case BookingScreen.Done:
					path = 'done';
					if (options.ticket) path += `/${options.ticket}`;
					if (options.reschedule) path += '?reschedule=true';
					break;
				case BookingScreen.Error:
					path = 'error';
					break;
			}
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.GoToScreen, analyticActionName);
			const url = [routeBase, 'booking', path].filter(Boolean);
			routerService.navigateByUrl(`/${url.join('/')}`, {
				replace: options.replace,
				state: options.autoForwardedFromLocation ? { bookingAutoForwardedFromLocation: true } : undefined,
			});
		},
		[analyticService, routeBase, routerService],
	);

	const getFlowSettings = useCallback((): FlowSettings => {
		if (flowSettings.status !== 'accessible') throw new Error('Flow settings not accessible yet.');
		return flowSettings.value;
	}, [flowSettings]);

	const getFlowData = useCallback((): FlowData => {
		if (flowData.status === 'accessible') return flowData.value;
		throw new Error("FlowData isn't accessible yet. Make sure to run whenMount() before trying to access flowData.");
	}, [flowData]);

	const getFilteredReasonForVisits = useCallback((): ClientConditionApiModel[] => {
		let filteredReasonForVisits: ClientConditionApiModel[] = [];
		const settings = getFlowSettings();
		const data = getFlowData();
		if (settings.reasonForVisit) {
			if (settings.reasonForVisit.length > 0) {
				filteredReasonForVisits = data.reasonForVisits.filter((rfv) => settings.reasonForVisit?.includes(rfv.Id)) ?? [];
			}
		} else {
			filteredReasonForVisits = data.reasonForVisits ?? [];
		}
		return filteredReasonForVisits;
	}, [getFlowSettings, getFlowData]);

	const getSelectedReasonForVisit = useCallback((): Nullable<ClientConditionApiModel> => {
		const selectedReasonForVisit = bookingBasket.getBasket().reasonForVisit;
		return getFilteredReasonForVisits().find((rfv) => rfv.Id === selectedReasonForVisit) ?? null;
	}, [bookingBasket.getBasket, getFilteredReasonForVisits]);

	const goToReceptionHome = useCallback(() => {
		// Client-side routerService.navigateByUrl leaves this flow's route tree stuck rendered
		// after leaving a flow route (e.g. this booking flow); a real URL change reliably resets it.
		// The router's basename (licence holder alias) isn't applied to window.location, so it's prepended here.
		window.location.href = getAbsoluteRoute([universalConfig.alias, routeBase].filter(Boolean).join('/'));
	}, [universalConfig.alias, routeBase]);

	const fetchSearchLimitation = useCallback(
		async (searchContextId: string): Promise<SearchLimitationResult> =>
			queryClient.fetchQuery({
				...searchLimitationGetSearchLimitationOptions({
					// The booking flow stores the selected reception in selectedUnitId.
					path: { receptionId: universalConfig.selectedUnitId },
					query: { limitationId: searchContextId },
				}),
				// A search context is immutable. Keep the setup and settings stages on the same response.
				staleTime: Number.POSITIVE_INFINITY,
			}),
		[queryClient, universalConfig.selectedUnitId],
	);

	const setupNewBasket = useCallback(async () => {
		analytic.trackEvent('Setup new booking');
		bookingBasket.clearBasket();
		bookingBasket.unlockTimeSlot();
		const redRobinSessionId = searchParams.get('redRobinSessionId');
		const deepLinkParams = {
			dom: searchParams.get('dom') ?? null,
			rfv:
				searchParams
					.get('rfv')
					?.split(',')
					.map((id) => safeNumberParse(id))
					.filter((id) => id != null) ?? null,
			attachFiles: searchParams.get('file') ? searchParams.get('file') !== 'false' : null,
			lockFilters: searchParams.get('lock') ? searchParams.get('lock') === 'true' : null,
			resourceId: searchParams.get('r') ? safeNumberParse(searchParams.get('r') ?? '') : null,
			specialityId: searchParams.get('s') ? safeNumberParse(searchParams.get('s') ?? '') : null,
			unitId: searchParams.get('u') ? safeNumberParse(searchParams.get('u') ?? '') : null,
			locationId: searchParams.get('l') ? safeNumberParse(searchParams.get('l') ?? '') : null,
			locationType: searchParams.get('lt') ?? null,
			fromDate: searchParams.get('f') ?? null,
			toDate: searchParams.get('t') ?? null,
			searchContextId: searchParams.get('searchContextId') ?? null,
			additionalParameters: Array.from(searchParams.keys())
				.filter((key) => key.startsWith('addParam[') && key.endsWith(']'))
				.reduce(
					(acc, key) => {
						const index = key.substring('addParam['.length, key.length - 1);
						const value = searchParams.get(key);
						if (value) acc[index] = value;
						return acc;
					},
					{} as Record<string, string>,
				),
		};
		let searchContext: SearchLimitationResult | null = null;
		if (deepLinkParams.searchContextId != null) {
			try {
				searchContext = await fetchSearchLimitation(deepLinkParams.searchContextId);
			} catch {
				loggerRef.current.logError('Failed to load booking search context');
				goToScreen(BookingScreen.Error, { replace: true });
				return;
			}
		}

		let representativeNin = null;
		if (redRobinSessionId && searchContext?.NavigationBookingHandoff == null) {
			const redRobinResult = await redRobinApiService.getRepresentativeNin(redRobinSessionId);
			if (redRobinResult.err) {
				loggerRef.current.logError('Failed to get RedRobin session');
				throw redRobinResult.err;
			}
			representativeNin = redRobinResult.ok.data.RepresentativeNin;
		}

		const settings: BookingSettings = {
			redRobinSessionId,
			asAgentFor: representativeNin ?? null,
			descriptionOfMatter: deepLinkParams.dom,
			reasonForVisit: deepLinkParams.rfv,
			attachFiles: deepLinkParams.attachFiles ?? true,
			lockFilters: deepLinkParams.lockFilters ?? false,
			filterResourceId: deepLinkParams.resourceId,
			filterSpecialityId: deepLinkParams.specialityId,
			filterUnitId: deepLinkParams.unitId,
			locationId: deepLinkParams.locationId,
			locationType: deepLinkParams.locationType,
			searchContextId: deepLinkParams.searchContextId,
			fromDate: deepLinkParams.fromDate ? dateService.newDate(deepLinkParams.fromDate) : null,
			toDate: deepLinkParams.toDate ? dateService.newDate(deepLinkParams.toDate) : null,
			additionalParameters: deepLinkParams.additionalParameters,
		};

		bookingSettings.setSettings(settings);
		const rescheduleTicket = searchParams.get('rescheduleTicket');
		if (rescheduleTicket) {
			analytic.trackEvent('Setup reschedule booking');
			bookingBasket.setBasket({ rescheduleTicket });
		}
		const subjectRelation = searchContext?.NavigationBookingHandoff?.SubjectRelation;
		if (subjectRelation != null) {
			let respondantType: 'me' | 'representative';
			if (subjectRelation === NavigationBookingSubjectRelation.AuthenticatedClient) {
				respondantType = 'me';
			} else if (subjectRelation === NavigationBookingSubjectRelation.RepresentedPatient) {
				respondantType = 'representative';
			} else {
				goToScreen(BookingScreen.Error, { replace: true });
				return;
			}

			await bookingBasket.setBasket({
				respondantType,
				asAgentFor: null,
			});
			goToScreen(BookingScreen.ReasonForVisit, { replace: true });
			return;
		}

		goToScreen(BookingScreen.WhoIsRespondant, { replace: true });
	}, [
		analytic.trackEvent,
		bookingBasket.clearBasket,
		bookingBasket.unlockTimeSlot,
		bookingBasket.setBasket,
		redRobinApiService,
		fetchSearchLimitation,
		dateService,
		bookingSettings.setSettings,
		goToScreen,
		searchParams.get,
		searchParams.keys,
	]);

	const abortBooking = useCallback(async () => {
		await bookingBasket.unlockTimeSlot();
		bookingBasket.clearBasket();
		goToReceptionHome();
	}, [bookingBasket.unlockTimeSlot, bookingBasket.clearBasket, goToReceptionHome]);

	const loadSettings = useCallback(async (): Promise<AsyncState<FlowSettings, FlowSettingsError>> => {
		loggerRef.current.logInformation('Load settings');
		const settings = bookingSettings.getSettings();
		let nextFlowSettings: FlowSettings = { ...settings, searchContext: null };
		if (settings.searchContextId != null) {
			let data: SearchLimitationResult;
			try {
				data = await fetchSearchLimitation(settings.searchContextId);
			} catch (requestError) {
				let settingsError: FlowSettingsError = 'Unknown';
				if (
					typeof requestError === 'object' &&
					requestError != null &&
					'statusCode' in requestError &&
					requestError.statusCode === 401
				) {
					settingsError = 'Unauthorized';
				}
				const result: AsyncState<FlowSettings, FlowSettingsError> = { value: settingsError, status: 'error' };
				setFlowSettings(result);
				return result;
			}
			const overrideSettings: Partial<BookingSettings> = {};
			if (data.HcpId) overrideSettings.filterResourceId = data.HcpId;
			if (data.PhysicalLocationId) overrideSettings.locationId = data.PhysicalLocationId;
			if (Object.keys(overrideSettings).length > 0) overrideSettings.lockFilters = true;
			nextFlowSettings = { ...settings, ...overrideSettings, searchContext: data };
		}
		const result: AsyncState<FlowSettings, FlowSettingsError> = { value: nextFlowSettings, status: 'accessible' };
		setFlowSettings(result);
		return result;
	}, [bookingSettings.getSettings, fetchSearchLimitation]);

	useEffect(() => {
		let cancelled = false;
		analyticService.trackPageView({ customTitle: 'Booking' });
		loggerRef.current.logInformation('Mounting useViewBooking');
		setFlowData({ value: null, status: 'pending' });

		(async () => {
			let unitConfig;
			try {
				unitConfig = await queryClient.fetchQuery(
					clientUnitsGetOptions({ path: { unitId: universalConfig.selectedUnitId, platform: 'web' } }),
				);
			} catch {
				setFlowData({ value: null, status: 'error' });
				return;
			}
			if (cancelled) return;
			if (!unitConfig.Data) {
				setFlowData({ value: null, status: 'error' });
				return;
			}
			const allowRepresentative = unitConfig.Data.Offer?.AllowClientAgents || false;
			const useReasonForVisits = unitConfig.Data.Offer?.HasConditionWorkflow || false;
			let reasonForVisits: ClientConditionApiModel[] = [];
			if (useReasonForVisits) {
				try {
					reasonForVisits = await fetchReasonForVisits();
				} catch {
					setFlowData({ value: null, status: 'error' });
					return;
				}
				if (cancelled) return;
			}
			let physicalLocations: PhysicalLocationApiModel[] = [];
			try {
				physicalLocations = await fetchPhysicalLocations();
			} catch {
				setFlowData({ value: null, status: 'error' });
				return;
			}
			if (cancelled) return;
			bookingBasket.loadBasket({ useReasonForVisits });
			const userNationality = authService.getUser()?.nationality;
			setFlowData({
				value: {
					currentCountry:
						userNationality && NiN.supportsCountryCode(userNationality)
							? (userNationality as keyof NiN.Implementations)
							: universalConfig.selectedCountryCode.peek(),
					allowRepresentative,
					useReasonForVisits,
					reasonForVisits,
					physicalLocations,
				},
				status: 'accessible',
			});
		})();

		return () => {
			cancelled = true;
		};
	}, [
		analyticService,
		queryClient,
		universalConfig.selectedUnitId,
		universalConfig.selectedCountryCode,
		fetchReasonForVisits,
		fetchPhysicalLocations,
		bookingBasket.loadBasket,
		authService,
	]);

	const value = useMemo<ViewBookingContextValue>(
		() => ({
			flowData,
			flowSettings,
			goToScreen,
			goToReceptionHome,
			setupNewBasket,
			abortBooking,
			loadSettings,
			getFlowSettings,
			getFlowData,
			getFilteredReasonForVisits,
			getSelectedReasonForVisit,
		}),
		[
			flowData,
			flowSettings,
			goToScreen,
			goToReceptionHome,
			setupNewBasket,
			abortBooking,
			loadSettings,
			getFlowSettings,
			getFlowData,
			getFilteredReasonForVisits,
			getSelectedReasonForVisit,
		],
	);

	return <ViewBookingContext.Provider value={value}>{children}</ViewBookingContext.Provider>;
}

export enum ViewBookingAnalyticActions {
	GoToScreen = 'Go-to-screen',
	NotAllowedToBeHere = 'Not allowed to be here',
	DoesNotAllowRepresentative = 'Does not allow representative',
	AutoSetRespondantTypeRepresentative = 'Automatically respondant type set to "representative"',
	AutoSetRespondantTypeMe = 'Automatically respondant type set to "me"',
	AutoSetReasonForVisit = 'Automatically set reason for visit',
	SelectReasonForVisit = 'Select reason for visit',
	NoReasonForVisits = 'No reason for visits',
	AutoSetLocation = 'Automatically set location',
	OnlyDigital = 'Only digital',
	SelectLocation = 'Select location',
	ErrorFetchingAvailability = 'Error fetching availability',
	ErrorFetchingTimeSlots = 'Error fetching time slots',
	OpenTimeSlotDetails = 'Open time slot details',
	CloseTimeSlotDetails = 'Close time slot details',
	ErrorFetchingTimeSlotDetails = 'Error fetching time slot details',
	ErrorWhileLockingTimeSlot = 'Error while locking time slot',
	SelectTimeSlot = 'Select time slot',
	SelectTimeSlotAndLogin = 'Select time slot and login',
	NoDynamicForms = 'No dynamic forms',
	NumberOfDynamicForms = 'Number of dynamic forms',
	ErrorFetchingDynamicForms = 'Error fetching dynamic forms',
	DoesNotUseRepresentatives = 'Does not use representatives',
	ErrorFetchingTimeSlot = 'Error fetching time slot',
	FormIsInvalid = 'Form is invalid',
	SelectedTimeSlotNotIsAvailable = 'Selected time slot is not available',
	OpenedTimeSlotNotAvailableDialog = 'Opened time slot not available dialog',
	ClosedTimeSlotNotAvailableDialog = 'Closed time slot not available dialog',
}
